import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Col,
  DatePicker,
  Flex,
  Popconfirm,
  Result,
  Row,
  Select,
  Space,
  Tooltip,
} from 'antd';
import { AxiosError } from 'axios';
import PaqueryTable from '@paquery-team/lib-table';
import { usePrepRoutes } from 'services/journeys/list';
import { useOffer } from 'services/journeys';
import { DEFAULT_PAGINATE } from 'constants/defaultValues';
import SITELINKS from 'constants/sitelinks';
import dayjs, { Dayjs } from 'dayjs';
import { TableDateTimeFormat } from 'constants/dateFormats';
import { formatDuration } from 'util/Tools';
import { useDrivers } from 'services/DriverService';
import EditRecordButton from 'components/editRecordButton';
import { Journey, JourneyKind, TimeWindow } from 'interfaces/journey';
import api from 'constants/api';
import Api from 'util/Api';
import {
  buildShipmentsById,
  FlaggedShipment,
  getFlaggedShipments,
} from 'services/journeys/shipmentHelpers';
import AuditLog from '../dashboard/components/AuditLog';
import OfferAtRiskModal from '../dashboard/components/OfferAtRiskModal';
import { Option } from '..';
import { KindSelect, RouteSearch, kindColumn, nameColumn } from './kind';
import FuseRoutesButton from '../dashboard/components/FuseRoutesButton';

const idColumn = {
  title: 'ID',
  dataIndex: 'id',
  key: 'id',
  align: 'center',
  width: '8%',
};

const shipmentAmountColumn = {
  title: '# envíos',
  key: 'totalPackages',
  dataIndex: 'totalPackages',
  responsive: ['lg'],
  align: 'center',
  width: '8%',
  render: (_value: number, journey: Journey) =>
    (journey.totalCollections ?? 0) > 0 ? (
      <Tooltip
        title={`${journey.totalPackages ?? 0} envíos y ${journey.totalCollections} colectas`}
      >
        {(journey.totalPackages ?? 0) + (journey.totalCollections ?? 0)}
      </Tooltip>
    ) : (
      (journey.totalPackages ?? '---')
    ),
};

const creationDateColumn = {
  title: 'Fecha de creación',
  key: 'date',
  dataIndex: 'creationDate',
  render: (date: Date) => dayjs(date).tz().format(TableDateTimeFormat),
  responsive: ['sm'],
};

const timeColumn = {
  title: 'Tiempo estimado',
  dataIndex: 'estimatedTime',
  render: (time: number) => formatDuration(time),
  key: 'time',
  responsive: ['md'],
};

const distanceColumn = {
  title: 'Distancia total',
  dataIndex: 'estimatedDistance',
  render: (distance: number) =>
    distance ? `${(distance / 1000).toFixed(2)} km` : 'Desconocido',
  key: 'distance',
  responsive: ['lg'],
};

// TODO turn into selector maybe?
const driverColumn = {
  title: 'Driver',
  key: 'driverName',
  dataIndex: 'driverName',
  render: (driverName: string) => driverName || 'Sin asignar',
  responsive: ['sm'],
};

const OfferButton = (journey: Journey) => {
  const [popconfirmOpen, setPopconfirmOpen] = useState(false);
  const [offerAtRiskModalOpen, setOfferAtRiskModalOpen] = useState(false);
  const [flaggedShipments, setFlaggedShipments] = useState<FlaggedShipment[]>(
    [],
  );
  const [schedule, setSchedule] = useState<TimeWindow | undefined>();
  const [isFetching, setIsFetching] = useState(false);
  const { mutate: offerRoute, isPending } = useOffer();

  const handleButtonClick = async () => {
    setIsFetching(true);
    try {
      if (!journey.id) return;
      const response = await Api.apiAxios.get(api.journeys.get(journey.id));
      if (!Api.isSuccessResponse(response)) return;

      const journeyDetail = response.data as Journey;
      if (!journeyDetail) return;

      const shipmentsById = buildShipmentsById(journeyDetail.shipments);
      const flagged = getFlaggedShipments(
        journeyDetail.waypoints,
        shipmentsById,
      );

      setFlaggedShipments(flagged);
      setSchedule(journeyDetail.schedule);

      if (flagged.length > 0) {
        setOfferAtRiskModalOpen(true);
      } else {
        setPopconfirmOpen(true);
      }
    } catch (error) {
      console.error('Error fetching journey details:', error);
    } finally {
      setIsFetching(false);
    }
  };

  const handleConfirmOffer = () => {
    if (journey.id) offerRoute(journey.id);
    setPopconfirmOpen(false);
    setOfferAtRiskModalOpen(false);
  };

  return (
    <>
      <Popconfirm
        destroyTooltipOnHide
        title="Confirmar operación"
        description={
          journey.driverName
            ? `¿Desea ofrecer esta ruta al driver ${journey.driverName}?`
            : '¿Desea ofertar esta ruta?'
        }
        onCancel={() => setPopconfirmOpen(false)}
        okText="Confirmar"
        placement="left"
        cancelText="Cancelar"
        onConfirm={handleConfirmOffer}
        open={popconfirmOpen}
      >
        <Button
          type="default"
          onClick={handleButtonClick}
          disabled={
            isPending ||
            isFetching ||
            (journey.totalPackages ?? 0) + (journey.totalCollections ?? 0) === 0
          }
          loading={isPending || isFetching}
        >
          {journey.driverName ? 'Ofrecer' : 'Ofertar'}
        </Button>
      </Popconfirm>
      <OfferAtRiskModal
        open={offerAtRiskModalOpen}
        items={flaggedShipments}
        routeSchedule={schedule}
        confirmLoading={isPending}
        hasDriver={!!journey.driverName}
        onCancel={() => setOfferAtRiskModalOpen(false)}
        onConfirm={handleConfirmOffer}
      />
    </>
  );
};

const editColumn = {
  key: 'edit',
  render: (journey: Journey) => {
    return (
      <Flex align="center" justify="center" gap={8}>
        <EditRecordButton link={SITELINKS.journey} record={journey} />
        <AuditLog mode="list" journeyId={journey.id} />
        {journey.kind !== 'collections' && (
          <FuseRoutesButton journeyId={journey.id} mode="list" />
        )}
        <OfferButton {...journey} />
      </Flex>
    );
  },
  width: '15%',
  align: 'center',
};

const dataColumns = [
  idColumn,
  nameColumn,
  kindColumn,
  shipmentAmountColumn,
  timeColumn,
  distanceColumn,
  driverColumn,
  creationDateColumn,
  editColumn,
];

const RoutesPrep = () => {
  const { data: drivers } = useDrivers();
  const [driverList, setDriverList] = useState<Option[]>([]);
  const [selectedDriver, setSelectedDriver] = useState<number>();
  const [selectedKind, setSelectedKind] = useState<JourneyKind>();
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [date, setDate] = useState<Dayjs>();
  const [search, setSearch] = useState<string>();
  const queries = useMemo(
    () => ({
      driverId: selectedDriver,
      kind: selectedKind,
      date,
      page,
      size,
      search,
    }),
    [page, selectedDriver, selectedKind, date, size, search],
  );
  const { data, isLoading, isFetching, error, refetch } =
    usePrepRoutes(queries);

  const pageable = useMemo(() => {
    return {
      // ponytail: el control lo maneja el estado local `page`; del server solo el total
      pageNumber: page,
      pageSize: size ?? DEFAULT_PAGINATE.pageSize,
      totalElements: data?.total_elements ?? DEFAULT_PAGINATE.totalElements,
    };
  }, [page, size, data]);

  const handleOnPaginate = (pagination: {
    pageNumber: number;
    pageSize: number;
  }) => {
    setPage(pagination.pageNumber);
    setSize(pagination.pageSize);
  };

  // ponytail: identidad estable, si no el effect del Searcher (allowEmptySearch)
  // dispara onSearching('') en cada render y resetea la página
  const handleSearch = useCallback((value?: string) => {
    setSearch(value || undefined);
    setPage(0);
  }, []);

  useEffect(() => {
    const driversOptions: Option[] = [
      {
        value: '0',
        name: 'Sin driver',
      },
    ];
    if (drivers && drivers.length > 0) {
      setDriverList(
        drivers.forEach(
          (driver: { id: number; name: string; lastName: string }) =>
            driversOptions.push({
              value: driver.id,
              name: `${driver.name} ${driver.lastName}`,
            }),
        ),
      );
    }
    setDriverList(driversOptions);
  }, [drivers]);

  const getResultStatus = (err: AxiosError) => {
    if (!err.response?.status) return 'error';
    if (
      err.response.status === 404 ||
      err.response.status === 403 ||
      err.response.status === 500
    )
      return err.response.status;
    return 'error';
  };

  const extraButtons = [
    <Button type="primary" onClick={() => refetch()}>
      Reintentar
    </Button>,
  ];

  return (
    <Row gutter={[16, 36]}>
      <Col xs={24}>
        {error && (
          <Result
            status={getResultStatus(error as AxiosError)}
            title="Algo salio mal..."
            subTitle={error.message}
            extra={extraButtons}
          />
        )}
        {!error && (
          <PaqueryTable
            header={{
              extraMainAction: (
                <Space>
                  <RouteSearch onSearch={handleSearch} />
                  <KindSelect onChange={setSelectedKind} />
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="Driver"
                    style={{ width: 200 }}
                    options={driverList.map((o) => ({
                      value: o.value,
                      label: o.name,
                    }))}
                    onChange={(value: number) => setSelectedDriver(value)}
                  />
                  <DatePicker
                    placeholder="Seleccione fecha de creación"
                    style={{ width: 220 }}
                    onChange={(date: Dayjs | null) =>
                      setDate(date ?? undefined)
                    }
                  />
                </Space>
              ),
              refresh: refetch,
              title: 'Preparación',
            }}
            onChangePaginate={handleOnPaginate}
            loading={isLoading}
            fetching={isFetching}
            dataSource={
              data && data.journeys?.map((j) => ({ ...j, id: j.journeyId }))
            }
            dataColumns={dataColumns}
            paginate={pageable}
            colsForSmallDevice={dataColumns}
            colsForMediumDevice={dataColumns}
            colsForLargeDevice={dataColumns}
          />
        )}
      </Col>
    </Row>
  );
};

export default RoutesPrep;
